import { useMutation, useQuery } from "@tanstack/react-query";
import { ServerInferRequest } from "@ts-rest/core";
import { Ledger, ledgerContract } from "@common/contracts/ledger.contract";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { useEffect, useMemo } from "react";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { useScope } from "@state/scopeContext.lib";
import { queryClient } from "../queryClient";
import { invalidateAccountRelatedQueries } from "./account.api";

const ledgerContractClient = getBackendAPIClient(ledgerContract);

const getLedgersQueryKey = (
    entityId?: number,
    {
        includeArchived = false,
        includeHasJournalEntries = false,
    }: {
        includeArchived?: boolean;
        includeHasJournalEntries?: boolean;
    } = {},
) => [
    "ledgers",
    entityId,
    {
        includeArchived,
        ...(includeHasJournalEntries ? { includeHasJournalEntries } : {}),
    },
];
const getLedgersByEntityIdsQueryKey = (
    entityIds: number[],
    includeArchived = false,
) => ["ledgers", "byEntityIds", entityIds, { includeArchived }];

export function useLedgers(
    entityId?: number,
    includeArchived = false,
    options?: { includeHasJournalEntries?: boolean },
) {
    const includeHasJournalEntries = options?.includeHasJournalEntries ?? false;
    return useQuery({
        queryKey: getLedgersQueryKey(entityId, {
            includeArchived,
            includeHasJournalEntries,
        }),
        queryFn: async () => {
            if (!entityId) {
                return [];
            }
            const result = await ledgerContractClient.getLedgersForEntity({
                params: { entityId },
                query: { includeArchived, includeHasJournalEntries },
            });
            return result.body.ledgers;
        },
        refetchOnMount: false,
        staleTime: 1000 * 60 * 30, // 30 minutes
    });
}

export function useLedgersByEntityIds(
    entityIds: number[],
    includeArchived = false,
) {
    const uniqueEntityIds = useMemo(
        () => [...new Set(entityIds)].filter((id) => !!id),
        [entityIds],
    );

    return useQuery({
        queryKey: getLedgersByEntityIdsQueryKey(
            uniqueEntityIds,
            includeArchived,
        ),
        queryFn: async (): Promise<Record<number, Ledger[]>> => {
            if (uniqueEntityIds.length === 0) {
                return {};
            }
            const result = await ledgerContractClient.getLedgersByEntityIds({
                query: { entityIds: uniqueEntityIds, includeArchived },
            });
            return result.body.ledgers;
        },
        refetchOnMount: false,
        staleTime: 1000 * 60 * 30,
    });
}

/**
 * Warms the ledger caches for a set of entities with a single batched request,
 * then seeds each entity's per-entity `useLedgers` cache from the result. This
 * lets accounting reports resolve a valid ledger synchronously when switching
 * entities (no per-switch fetch), avoiding the brief "withhold until ledgers
 * load" wait. Safe to mount once for the whole accounting section.
 */
export function useWarmLedgerCaches(entityIds: number[]) {
    const { data: ledgersByEntityId } = useLedgersByEntityIds(entityIds);

    useEffect(() => {
        if (!ledgersByEntityId) {
            return;
        }
        for (const [entityId, ledgers] of Object.entries(ledgersByEntityId)) {
            queryClient.setQueryData(
                getLedgersQueryKey(Number(entityId)),
                ledgers,
            );
        }
    }, [ledgersByEntityId]);
}

// Fails open to `true` while the query is loading or errored, so callers that
// gate on an accrual ledger don't briefly treat the entity as cash-only.
export function useAccrualLedgerStatus(entityId?: number) {
    const { data: ledgers, isLoading, isError } = useLedgers(entityId);
    const hasAccrualLedger = useMemo(
        () =>
            isLoading || isError
                ? true
                : (ledgers?.some(
                      (l) => l.basis === AccountingLedgerBasis.accruals,
                  ) ?? false),
        [ledgers, isLoading, isError],
    );
    return { hasAccrualLedger, isLoading, isError };
}

export function useHasAccrualLedger(entityId?: number) {
    return useAccrualLedgerStatus(entityId).hasAccrualLedger;
}

// Fails open to `true` while loading or errored, like `useAccrualLedgerStatus`.
export function useAnyEntityHasAccrualLedger(entityIds: number[]) {
    const {
        data: ledgersByEntityId,
        isLoading,
        isError,
    } = useLedgersByEntityIds(entityIds);
    return useMemo(
        () =>
            isLoading || isError
                ? true
                : Object.values(ledgersByEntityId ?? {}).some((ledgers) =>
                      ledgers.some(
                          (ledger) =>
                              ledger.basis === AccountingLedgerBasis.accruals,
                      ),
                  ),
        [ledgersByEntityId, isLoading, isError],
    );
}

export function useCreateLedger() {
    const { workspaceId } = useScope();

    return useMutation({
        mutationFn: async ({
            params,
            body,
        }: ServerInferRequest<typeof ledgerContract.createLedgerForEntity>) => {
            const response = await ledgerContractClient.createLedgerForEntity({
                params: { entityId: params.entityId },
                body,
            });
            return response.body.ledger;
        },
        onSuccess: async (_, { params }) => {
            const entityId = Number(params.entityId);
            invalidateLedgerQueries(entityId);
            invalidateAccountRelatedQueries({
                entityIds: [entityId],
                workspaceId,
            });
        },
    });
}

export function useUpdateLedger() {
    return useMutation({
        mutationFn: async ({
            params,
            body,
        }: ServerInferRequest<typeof ledgerContract.updateLedger>) => {
            const response = await ledgerContractClient.updateLedger({
                params,
                body,
            });
            return response.body.ledger;
        },
        onSuccess: async (_, { params }) => {
            invalidateLedgerQueries(Number(params.entityId));
        },
    });
}

export function useSetLedgerStatus() {
    return useMutation({
        mutationFn: async ({
            params,
            body,
        }: ServerInferRequest<typeof ledgerContract.setLedgerStatus>) => {
            const response = await ledgerContractClient.setLedgerStatus({
                params,
                body,
            });
            return response.body.ledger;
        },
        onSuccess: async (_, { params }) => {
            invalidateLedgerQueries(Number(params.entityId));
        },
    });
}

export function useDuplicateLedger() {
    const { workspaceId } = useScope();

    return useMutation({
        mutationFn: async ({
            params,
            body,
        }: ServerInferRequest<typeof ledgerContract.duplicateLedger>) => {
            const response = await ledgerContractClient.duplicateLedger({
                params,
                body,
            });
            return response.body.ledger;
        },
        onSuccess: async (_, { params }) => {
            const entityId = Number(params.entityId);
            invalidateLedgerQueries(entityId);
            invalidateAccountRelatedQueries({
                entityIds: [entityId],
                workspaceId,
            });
        },
    });
}

/**
 * Resolves which entity ids in the input have an accrual ledger and exposes
 * the underlying query loading state. Consumers that branch on accrual vs.
 * non-accrual rendering (e.g. open-amount math) need `isLoading` so they can
 * suppress incorrect figures during the in-flight window where
 * `accrualEntityIds` is still empty.
 */
export function useAccrualEntityIdsWithLoading(entityIds: number[]) {
    const uniqueEntityIds = useMemo(
        () => [...new Set(entityIds)].filter((id) => !!id),
        [entityIds],
    );

    const { data: ledgersByEntityId, isLoading } =
        useLedgersByEntityIds(uniqueEntityIds);

    const accrualEntityIds = useMemo(
        () =>
            new Set(
                uniqueEntityIds.filter((entityId) =>
                    (ledgersByEntityId?.[entityId] ?? []).some(
                        (ledger) =>
                            ledger.basis === AccountingLedgerBasis.accruals,
                    ),
                ),
            ),
        [uniqueEntityIds, ledgersByEntityId],
    );

    return {
        accrualEntityIds,
        // No request is fired when there are no entity ids to look up, so
        // we should not report the empty-input case as "loading".
        isLoading: uniqueEntityIds.length > 0 && isLoading,
    };
}

export function useEntityIdsWithAccrualLedger(entityIds: number[]) {
    return useAccrualEntityIdsWithLoading(entityIds).accrualEntityIds;
}

export function useHasAnyAccrualLedger(entityIds: number[]) {
    const entityIdsWithAccrual = useEntityIdsWithAccrualLedger(entityIds);
    return entityIdsWithAccrual.size > 0;
}

function invalidateLedgerQueries(entityId: number) {
    queryClient.invalidateQueries({
        queryKey: ["ledgers", entityId],
    });
    queryClient.invalidateQueries({
        queryKey: ["ledgers", "byEntityIds"],
    });
}
